import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, SkeletonWrapper } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetFleetDriversQuery } from "@/redux/apis/driver/driverApi";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { useGetUserAccountsQuery } from "@/redux/apis/userAccount/userAccountApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { GettingStartedSection } from "@/redux/slices/onboarding/types";
import { clearSoloScenario, selectSoloScenario, setSoloScenario } from "@/redux/slices/quickSetup";
import { SetupPanelItem, WalkthroughModal, ReadSetupGuideModal, WhyDoINeedModal } from "../QuickSetup/common";
import { quickSetupContent, quickSetupExplanations } from "../QuickSetup/data/quickSetupContent";
import { isSectionComplete } from "../QuickSetup/helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../QuickSetup/hooks";
import { StaticSetupPanel } from "./StaticSetupPanel";
import {
  AllSetCard,
  DownloadDriverAppStep,
  DriverAppCallout,
  DriverAppStepCard,
  HowYouWillUseRideMinder,
  OnboardingGuideCard,
  PlatformPill,
  WelcomeHeader,
} from "./components";
import { SoloScenarioId } from "./data/driverAppScenarios";
import { driverAppSteps } from "./data/driverAppSteps";
import { isSoloStripeValid } from "./helpers/soloStepCompletion";
import { useSoloStepCompletion } from "./hooks/useSoloStepCompletion";

enum ModalType {
  STRIPE_WALKTHROUGH = "stripe-walkthrough",
  STRIPE_DOCUMENTATION = "stripe-documentation",
  STRIPE_WHY = "stripe-why-do-i-need",
}

const STEP_NUMBERS = [1, 2, 3, 4, 5, 6];

const stripeFeaturedIcon = {
  name: "stripe",
  background: "bg-[#6772E5]",
  isCustom: true,
};

export const SoloOperatorGetStartedPanel = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  // NOTE: pre-existing, tracked separately — this is hardcoded, so the SkeletonWrapper below never
  // renders its skeleton. Left as-is deliberately; out of scope for DEV-1419.
  const isLoading = false;
  const operator = useOperator();
  const { providerCode, registerLinks, id: operatorId } = operator;

  const { data: onboardingStatus } = useGetOnboardingStatusQuery(undefined, {
    pollingInterval: 60000,
    refetchOnFocus: true,
  });
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isStripeCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.gettingStarted.stripe) : false;
  const areLaterStepsLocked = !isSoloStripeValid(onboardingStatus);

  const { data: userAccountsData, isLoading: isLoadingUserAccounts } = useGetUserAccountsQuery({});
  const ownerAccount = userAccountsData?.data.items.find((item) => (item.role as string) === "Owner");

  const { data: fleetData, isLoading: isLoadingFleet } = useGetFleetDriversQuery({
    provider_uuid: operatorId,
    page: "1",
    page_size: "1",
    account_status: ["active", "Approved"],
  });
  const driver = fleetData?.data.drivers[0];
  const hasDriver = !!driver;

  const { completionByStep, isLoading: isLoadingCompletion } = useSoloStepCompletion(onboardingStatus);

  const selectedScenario = useAppSelector(selectSoloScenario(operatorId));
  const handleSelectScenario = (scenario: SoloScenarioId) => dispatch(setSoloScenario({ operatorId, scenario }));
  const handleClearScenario = () => dispatch(clearSoloScenario(operatorId));

  const completedByStep: Record<number, boolean> = useMemo(
    () => ({ 1: isStripeCompleted, 2: hasDriver, ...completionByStep }),
    [isStripeCompleted, hasDriver, completionByStep]
  );
  const isAllComplete = STEP_NUMBERS.every((step) => completedByStep[step]);
  const isBannerDataLoading = isLoadingUserAccounts || isLoadingFleet;

  const [openPanels, setOpenPanels] = useState<Set<number>>(new Set([1]));
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (isLoadingFleet || isLoadingCompletion || !onboardingStatus) return;
    hasInitialized.current = true;
    const firstIncomplete = STEP_NUMBERS.find((step) => !completedByStep[step]);
    setOpenPanels(firstIncomplete ? new Set([firstIncomplete]) : new Set());
  }, [isLoadingFleet, isLoadingCompletion, onboardingStatus, completedByStep]);

  const handlePanelToggle = (stepNumber: number) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  };

  const handleModalClose = () => setActiveModal(null);

  const handleStripeSetup = () => {
    setQuickSetupFlag(GettingStartedSection.STRIPE);
    navigate("/payments/payment-configuration");

    if (onboardingStatus) {
      const stripeSection = onboardingStatus.gettingStarted.stripe;
      if (stripeSection && !isSectionComplete(stripeSection)) {
        updateSectionStatus({ section: GettingStartedSection.STRIPE, completed: true }).catch((error) => {
          console.error("Failed to update section status:", error);
        });
      }
    }
  };

  return (
    <SkeletonWrapper isLoading={isLoading} skeleton={<div>Loading...</div>}>
      {/* items-stretch is load-bearing: it is what gives the reference pane's left border its full
          height and lets the guide strip sit flush on the pane's bottom edge. */}
      <div className="bg-neutral-surface-gray">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-stretch xl:gap-0">
          {/* The pane stays fluid so the reference pane keeps its edge, but the steps hold a 746px
              reading width, so a wide viewport grows the gutters rather than stretching the cards. */}
          <div className="flex min-w-0 flex-1 flex-col px-3 pt-4 lg:px-5 xl:pr-5">
            <div className="mx-auto flex w-full max-w-[746px] flex-col gap-5">
              <WelcomeHeader />

              {isAllComplete && (
                <AllSetCard
                  ownerName={ownerAccount?.name}
                  ownerEmail={ownerAccount?.email}
                  driver={driver}
                  isLoading={isBannerDataLoading}
                />
              )}

              <StaticSetupPanel
                stepNumber={1}
                title="Setup Payments"
                stepCount={1}
                completedSteps={isStripeCompleted ? 1 : 0}
                badge={<PlatformPill platform="web" />}
                isOpen={openPanels.has(1)}
                onToggle={() => handlePanelToggle(1)}
              >
                <SetupPanelItem
                  icon={stripeFeaturedIcon.name}
                  iconBackground={stripeFeaturedIcon.background}
                  isCustomIcon={stripeFeaturedIcon.isCustom}
                  title="Connect Stripe to get paid"
                  isCompleted={isStripeCompleted}
                  actions={[{ label: "Setup Stripe", onClick: handleStripeSetup, variant: "primary" }]}
                  helpOptions={[
                    {
                      label: "Why do I need to do this?",
                      icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
                      onClick: () => setActiveModal(ModalType.STRIPE_WHY),
                    },
                    { label: "Video Walkthrough", icon: "Video", onClick: () => setActiveModal(ModalType.STRIPE_WALKTHROUGH) },
                    { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.STRIPE_DOCUMENTATION) },
                  ]}
                />
              </StaticSetupPanel>

              <StaticSetupPanel
                stepNumber={2}
                title="Download the RideMinder Driver App & Set Up Your Account"
                stepCount={2}
                completedSteps={hasDriver ? 2 : 0}
                badge={<PlatformPill platform="mobile" />}
                isOpen={openPanels.has(2)}
                isLocked={areLaterStepsLocked}
                onToggle={() => handlePanelToggle(2)}
              >
                <DownloadDriverAppStep
                  providerCode={providerCode}
                  driverRegisterLink={registerLinks?.driver}
                  operatorEmail={ownerAccount?.email}
                  selectedScenario={selectedScenario}
                  onSelectScenario={handleSelectScenario}
                  onClearScenario={handleClearScenario}
                />
              </StaticSetupPanel>

              <DriverAppCallout />

              {driverAppSteps.map((step) => (
                <DriverAppStepCard
                  key={step.stepNumber}
                  step={step}
                  isCompleted={completedByStep[step.stepNumber]}
                  isOpen={openPanels.has(step.stepNumber)}
                  isLocked={areLaterStepsLocked}
                  onToggle={() => handlePanelToggle(step.stepNumber)}
                />
              ))}
            </div>
          </div>

          {/* Deliberately not sticky and not height-capped: a pane spanning the row's full height
              cannot usefully stick, and an internally scrolled one reserves a scrollbar gutter that
              narrows it below 384px and can leave the guide strip unreachable. Keeping the guide
              strip visible is the strip's own job — it is sticky to the viewport bottom — so the
              pane does not need to become a scroll container to get it. */}
          <aside className="flex w-full shrink-0 flex-col gap-8 border-t border-neutral-mid-gray bg-white pt-4 xl:w-[384px] xl:border-l xl:border-t-0">
            <div className="px-3">
              <HowYouWillUseRideMinder />
            </div>
            <OnboardingGuideCard />
          </aside>
        </div>
      </div>

      <WalkthroughModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Stripe Setup)"
        videoUrl={quickSetupContent.stripe.setup.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_DOCUMENTATION}
        onClose={handleModalClose}
        title="Stripe Setup Guide"
        documentationUrl={quickSetupContent.stripe.setup.documentationUrl}
        pdfUrl={quickSetupContent.stripe.setup.pdfUrl}
      />

      <WhyDoINeedModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_WHY}
        onClose={handleModalClose}
        title="Why do I need to setup Stripe?"
        explanations={quickSetupExplanations.stripe.setup}
      />
    </SkeletonWrapper>
  );
};
